<template>
    <div class="w-full border-t border-t-border/40 bg-gray-100 px-4 py-3">
        <div class="flex flex-col md:flex-row md:gap-4">
            <img
                :alt="productDisplayName(productStore.selectedProduct.product)"
                :src="overviewImageSrc"
                :srcset="overviewImageSrcset"
                :sizes="overviewImageSizes"
                class="aspect-square w-full rounded-md object-contain md:w-2/5 md:shrink-0 md:self-start"
            />

            <div class="mt-3 flex flex-1 flex-col justify-between gap-3 md:mt-0">
                <ProductOptionsPriceSummary :product-label="productStore.selectedProduct.product.label" />

                <!-- Desktop buttons (md+) -->
                <div class="hidden w-full gap-3 md:flex">
                    <Button class="h-14 w-14 shrink-0 md:h-14 md:w-14" no-tooltip only-icon task="back" @click="$emit('close')" />
                    <BigButton
                        data-test-id="product-options-add-to-cart-button"
                        :label="addToCartButtonLabel"
                        :loading="isLoading"
                        class="flex-1 bg-order-buttons font-extrabold text-white"
                        no-tooltip
                        task="addToCart"
                        @click="handleAddToCart"
                    />
                </div>
            </div>
        </div>
    </div>

    <!-- Mobile sticky buttons (below md), sticky instead of fixed because the modal body scrolls -->
    <div class="sticky bottom-0 z-20 w-full border-t border-t-border/60 bg-white px-4 py-3 md:hidden">
        <div class="flex w-full items-center gap-4">
            <Button class="h-14 w-14 shrink-0 md:h-14 md:w-14" no-tooltip only-icon task="back" @click="$emit('close')" />
            <BigButton
                data-test-id="product-options-add-to-cart-button"
                :label="addToCartButtonLabel"
                :loading="isLoading"
                class="flex-1 bg-order-buttons font-extrabold text-white"
                no-tooltip
                task="addToCart"
                @click="handleAddToCart"
            />
        </div>
    </div>

    <AddToCartErrorModal
        v-model:open="cartError"
        :description="$t('pages.product_detail_page.product_info.product_button.add_to_cart.error')"
        @retry="handleAddToCart"
    />
</template>

<script lang="ts" setup>
import BigButton from '@/components/buttons/BigButton.vue';
import Button from '@/components/buttons/Button.vue';
import AddToCartErrorModal from '@/components/cart/partials/AddToCartErrorModal.vue';
import ProductOptionsPriceSummary from '@/components/product-options/ProductOptionsPriceSummary.vue';
import { useAddToCart } from '@/composables/cart/useAddToCart';
import { useProductOptionsTotals } from '@/composables/product-options/useProductOptionsTotals';
import { productDisplayName } from '@/lib/products/filter';
import { useAiGenerationHistoryStore } from '@/stores/ai-upload/AiGenerationHistoryStore';
import { useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { buildImageSrcset, ImageTypeEnum, resolvedImageUrl } from '@/utils/imageUrl';
import { breakpointsTailwind } from '@vueuse/core';
import _ from 'lodash';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

defineEmits<{
    close: [];
}>();

const { t } = useI18n();

const productStore = useProductStore();
const session = useAiImageGenerationSessionStore();
const historyStore = useAiGenerationHistoryStore();

const generatedImageUrl = computed<string | undefined>(() => {
    if (!productStore.isAiProduct) {
        return undefined;
    }

    const mostRecentEntry = _.orderBy(historyStore.entries, 'createdAt', 'desc')[0];
    return session.firstReadyBeautyShot?.resultUrl ?? mostRecentEntry?.beautyShots[0]?.resultUrl;
});

const overviewImageSrc = computed<string | undefined>(() => {
    if (generatedImageUrl.value) {
        return generatedImageUrl.value;
    }

    const image = productStore.selectedProductImages[0];
    return image ? resolvedImageUrl(image, ImageTypeEnum.Laptop) : undefined;
});

// Configured per magento group / configurable product in Joost; falls back to
// the per-locale default when nothing is set.
const addToCartButtonLabel = computed<string>(
    () => productStore.configurableProduct.add_to_cart_button_label || t('components.buttons.default.add_to_cart.label'),
);

const overviewImageSrcset = computed<string | undefined>(() => {
    if (generatedImageUrl.value) {
        return undefined;
    }

    const image = productStore.selectedProductImages[0];
    return image ? buildImageSrcset(image) : undefined;
});

// Full width in the phone sheet, 2/5 of the xl modal (max-w-7xl) from `md` up. Without
// this the browser assumes 100vw and downloads the 3000px variant for a 512px thumbnail.
const overviewImageSizes = `(max-width: ${breakpointsTailwind.md}px) calc(100vw - 24px), 512px`;

const { selectedOptionSkus } = useProductOptionsTotals();

const { addToCart, isLoading, cartError } = useAddToCart();

function handleAddToCart(): void {
    void addToCart(productStore.configurableProduct.group_identifier, productStore.selectedProduct.product.sku, 1, selectedOptionSkus.value);
}
</script>
